import { useState, useEffect, useCallback } from "react";
import type {
  CurrentEvaluation,
} from "@services/evaluationService";
import styles from "./EvaluationCard.module.css";

import { ATTEMPT_STATUS, EVALUATION_RESULT_LABELS, type EvaluationResult, type AttemptStatus, ATTEMPT_STATUS_OPTIONS } from "@constants/evaluationConstants";


export interface SMEFormData {
  marks: string;
  remarks: string;
  attemptStatus?:AttemptStatus;
}

interface SMEEvaluationFormProps {
  currentEvaluation?: CurrentEvaluation | null;
  minMarks: number;
  maxMarks: number;
  isOverrideMode?: boolean;
  onChange: (data: SMEFormData, isValid: boolean) => void;
}

function L1SMEEvaluationForm({
  currentEvaluation,
  minMarks,
  maxMarks,
  isOverrideMode = true,
  onChange,
}: SMEEvaluationFormProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // If currentEvaluation exists and we're not in override mode, pre-fill with it
  const initialMarks =
    currentEvaluation && !isOverrideMode
      ? String(currentEvaluation.awarded_marks)
      : "";
  const initialRemarks =
    currentEvaluation && !isOverrideMode
      ? currentEvaluation.feedbacks.join(" ") || ""
      : "";

  const [marks, setMarks] = useState(initialMarks);
  const [remarks, setRemarks] = useState(initialRemarks);
  const [marksError, setMarksError] = useState<string | null>(null);
  const [remarksError, setRemarksError] = useState<string | null>(null);
  const [touched, setTouched] = useState({ marks: false, remarks: false });
  const [attemptStatus, setAttemptStatus] = useState<AttemptStatus>(currentEvaluation?.attempt_status || ATTEMPT_STATUS.ATTEMPTED);
  const isUnattempted=attemptStatus===ATTEMPT_STATUS.UNATTEMPTED;

  // Reset form when currentEvaluation changes or override mode changes
  useEffect(() => {
    if (currentEvaluation) {
      setMarks(String(currentEvaluation.awarded_marks));
      setRemarks(currentEvaluation.feedbacks.join(" ") || "");
      setAttemptStatus(currentEvaluation?.attempt_status);
      setTouched({ marks: false, remarks: false });
      setMarksError(null);
      setRemarksError(null);
    }
  }, [currentEvaluation]);

  const validateMarks = useCallback(
    (value: string): string | null => {
      if (!value.trim()) {
        return "Marks is required";
      }
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        return "Please enter a valid number";
      }
      if (numValue < minMarks) {
        return `Marks must be at least ${minMarks}`;
      }
      if (numValue > maxMarks) {
        return `Marks must not exceed ${maxMarks}`;
      }
      return null;
    },
    [minMarks, maxMarks]
  );

  const validateRemarks = useCallback((value: string): string | null => {
    if (!value.trim()) {
      return "Remarks is required";
    }
    if (value.trim().length < 2) {
      return "Remarks must be at least 2 characters";
    }
    return null;
  }, []);

  useEffect(() => {
    const marksValidation = validateMarks(marks);
    const remarksValidation = validateRemarks(remarks);

    if (touched.marks) {
      setMarksError(marksValidation);
    }
    if (touched.remarks) {
      setRemarksError(remarksValidation);
    }
    if(isUnattempted){
      onChange({ marks: "", remarks: "", attemptStatus: attemptStatus! }, true);
      return;
    }
    const isValid = !marksValidation && !remarksValidation;
    onChange({ marks, remarks, attemptStatus: attemptStatus! }, isValid);
  }, [
    marks,
    remarks,
    attemptStatus,
    touched,
    validateMarks,
    validateRemarks,
    onChange,
    isUnattempted,
  ]);

  const handleMarksChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty, numbers, and decimals
    if (value === "" || /^-?\d*\.?\d*$/.test(value)) {
      setMarks(value);
    }
  };

  const handleMarksBlur = () => {
    setTouched((prev) => ({ ...prev, marks: true }));
  };

  const handleRemarksChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // if (isReadOnly) return;
    setRemarks(e.target.value);
  };

  const handleRemarksBlur = () => {
    // if (isReadOnly) return;
    setTouched((prev) => ({ ...prev, remarks: true }));
  };

  const handleAttempStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as AttemptStatus;
    setAttemptStatus(value);
    if(value===ATTEMPT_STATUS.UNATTEMPTED){
      setMarks("");
      setRemarks("");
      setTouched((prev) => ({ ...prev, marks: false, remarks: false }));
    }
  }

  return (
    <div className={styles.contentSection}>
      {/* SME Evaluation Form */}
      <div className={styles.smeCard}>
        <div className={styles.smeHeader}>
          <span className={styles.smeTitle}>SME Evaluation</span>
        </div>

        <div className={styles.formContainer}>
          <div className={styles.marksSection}>
            <div className={styles.marksInputRow}>
              <input
                type="text"
                className={
                  marksError && touched.marks
                    ? styles.marksInputError
                    : styles.marksInput
                }
                value={marks}
                onChange={handleMarksChange}
                onBlur={handleMarksBlur}
                placeholder="0"
                aria-label="Marks"
              />
              <span className={styles.maxMarks}>/ {maxMarks}</span>
            </div>
            {marksError && touched.marks ? (
              <span className={styles.errorText}>{marksError}</span>
            ) : (
              <span className={styles.helperText}>
                  "Enter marks based on the rubric criteria"
              </span>
            )}
          </div>
          <div className={styles.attempStatusStyle}>
          <label>Evaluation Status: </label>
          <select value={attemptStatus} onChange={handleAttempStatusChange}>
            {ATTEMPT_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
          {!isUnattempted && <div className={styles.remarksSection}>
            <label className={styles.remarksLabel}>Remarks</label>
            <textarea
              className={
                remarksError && touched.remarks
                  ? styles.remarksTextareaError
                  : styles.remarksTextarea
              }
              value={remarks}
              onChange={handleRemarksChange}
              onBlur={handleRemarksBlur}
              placeholder="Type remarks here"
              aria-label="Remarks"
            />
            {remarksError && touched.remarks && (
              <span className={styles.errorText}>{remarksError}</span>
            )}
          </div>}
        </div>
      </div>
    </div>
  );
}

export default L1SMEEvaluationForm